import { PropsWithChildren, useEffect, useMemo } from 'react';
import * as Sentry from '@sentry/nextjs';
import { useQueryClient } from '@tanstack/react-query';
import { usePreservedCallback, usePrevious } from '@toss/react';
import { isEqual } from 'lodash-es';
import { match, P } from 'ts-pattern';
import { createContext } from 'use-context-selector';
import { QUERY_KEY_ME, useMe } from '@zep/apis';
import { logger } from '@zep/core-logger';
import { getApplicationSessionId, getBrowserSessionId, getCorrelationId, getDeviceId } from '@zep/session-id';
import { SESSION_STORAGE_KEY, User } from '@zep/types';
import { getDeviceInformation } from '@zep/utils';
import { removeUserSession, setUserSession } from '../services';
import { ASP_SESSION_QUERY_KEY, useAspSessionQuery } from '../services/useAspSessionQuery';
type AuthContextType = {
  user: User | null;
  isInitialized: boolean;
  updateUser: (user: User) => Promise<void>;
  clearUser: () => void;
  refetch: ReturnType<typeof useMe>['refetch'];
};
export const AuthContext = createContext<AuthContextType | null>(null);
type AuthProviderProps = PropsWithChildren<{
  initialUser: User | undefined;
}>;
const NOT_EXISTS = P.nullish;
const EXISTS = P.not(P.nullish);
export const AuthProvider = ({
  children,
  initialUser
}: AuthProviderProps) => {
  const {
    data: aspSession,
    isInitialLoading: isAspSessionInitialLoading,
    isError: isAspSessionError
  } = useAspSessionQuery();
  const {
    data: user,
    isError,
    isInitialLoading: isUserInitialLoading,
    refetch
  } = useMe({
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    enabled: !!aspSession,
    initialData: initialUser,
    retry: false,
    suspense: false,
    useErrorBoundary: false
  });
  const queryClient = useQueryClient();
  const prevUser = usePrevious(user);

  //****************************************************************************************************
  const updateUser = usePreservedCallback(async (newUser: User) => {
    try {
      await setUserSession(newUser);
      queryClient.setQueryData(QUERY_KEY_ME, newUser);
    } catch (e) {
      console.error('Failed to set user session:', e);
    }
  });
  const clearUser = usePreservedCallback(() => {
    sessionStorage.removeItem(SESSION_STORAGE_KEY.PLAY_SESSION);
    queryClient.setQueryData(ASP_SESSION_QUERY_KEY, null);
    queryClient.setQueryData(QUERY_KEY_ME, null);
    removeUserSession().catch(error => {
      console.error('Failed to remove user session:', error);
    });
  });

  //****************************************************************************************************

  // 앱 구동시 초기 로그인 여부 판단, 유저 정보 가져오기 끝났는지 여부
  const isInitialized = useMemo(() => match([isAspSessionInitialLoading, aspSession, isUserInitialLoading]).with([false, null, P.any], () => true) // AspSession 초기 로딩 끝, AspSession이 없음 => 로그인 안된 유저
  .with([false, EXISTS, false], () => true) // AspSession이 있고, User 초기 로딩이 끝난 경우 => 로그인 한 유저
  .otherwise(() => false), [isAspSessionInitialLoading, aspSession, isUserInitialLoading]);

  // 유저 정보가 변경 되었을 때
  // prevUser => 기존 유저 데이터
  // user => 새로운 유저 데이터
  useEffect(() => {
    match([prevUser, user]).with([NOT_EXISTS, NOT_EXISTS], () => {
      // do nothing
      updateUserInfoForExternalTools(null);
    }).with([NOT_EXISTS, EXISTS], ([_, cur]) => {
      updateUser(cur);
      updateUserInfoForExternalTools(cur);
    }).with([EXISTS, NOT_EXISTS], () => {
      clearUser();
      updateUserInfoForExternalTools(null);
    }).with([EXISTS, EXISTS], ([prev, cur]) => {
      updateUserInfoForExternalTools(cur);
      if (!isEqual(prev, cur)) {
        updateUser(cur);
      }
    });
  }, [clearUser, prevUser, updateUser, user]);
  useEffect(() => {
    if (isError || isAspSessionError) {
      clearUser();
      // if (Router.basePath === '/play') {
      //   Router.reload();
      // }
    }
  }, [clearUser, isError, isAspSessionError]);
  useInitSentryTagEffect();
  useInitDeviceInfoEffect();
  return <AuthContext.Provider value={{
    user: user ?? null,
    isInitialized,
    updateUser,
    clearUser,
    refetch
  }} data-sentry-element="unknown" data-sentry-component="AuthProvider" data-sentry-source-file="AuthContext.tsx">
      {children}
    </AuthContext.Provider>;
};
function updateUserInfoForExternalTools(user: User | null) {
  if (user == null) {
    Sentry.setUser(null);
    logger.setState({
      // 주의: 데대보에서 사용하는 프로퍼티로 변경 시 기능에 문제가 생길 수 있습니다.
      // playerName: null 일경우 데대보 유니크로 집계하기가 어렵습니다. 해당 유니크 집계를 위해 해당 고유 세션아이디를 주입합니다
      playerEmail: getBrowserSessionId(),
      // TODO(logger-migration): 익명 식별 스키마 재설계 전까지 main과 동일한 기준 유지
      userEmail: getDeviceId() ?? 'anonymous'
    });
    return;
  }
  Sentry.setUser({
    id: user.hashId,
    email: user.snsId
  });
  logger.setState({
    playerEmail: user.snsId,
    playerName: user.name,
    playerHashId: user.hashId,
    userEmail: user.snsId,
    userName: user.name,
    userId: user.hashId
  });
  logger.identify(user.snsId);
}
function useInitSentryTagEffect() {
  useEffect(() => {
    const applicationSessionId = getApplicationSessionId();
    const browserSessionId = getBrowserSessionId();
    const deviceId = getDeviceId();
    const correlationId = getCorrelationId();
    if (applicationSessionId) {
      logger.setTag('zep_application_session_id', applicationSessionId);
    }
    if (browserSessionId) {
      logger.setTag('zep_browser_session_id', browserSessionId);
    }
    if (deviceId) {
      logger.setTag('zep_device_id', deviceId);
    }
    if (correlationId) {
      logger.setTag('zep_correlation_id', correlationId);
    }
    const commitSha = process.env.NEXT_PUBLIC_COMMIT_SHA;
    if (commitSha) {
      logger.setTag('zep_deployment_commit_id', commitSha);
    }
  }, []);
}
function useInitDeviceInfoEffect() {
  useEffect(() => {
    const deviceInformation = getDeviceInformation();
    const deviceId = getDeviceId();
    logger.setState({
      zep_user_agent: window.navigator.userAgent,
      zep_browser_name: deviceInformation.browser.name,
      zep_browser_version: deviceInformation.browser.version,
      zep_os_name: deviceInformation.os.name,
      zep_os_version: deviceInformation.os.version,
      zep_device_type: deviceInformation.device.type,
      zep_device_vendor: deviceInformation.device.vendor,
      zep_device_model: deviceInformation.device.model,
      zep_is_whale_browser: deviceInformation.isWhaleBrowser,
      // 주의: 데대보에서 사용하는 프로퍼티로 변경 시 기능에 문제가 생길 수 있습니다.
      zep_is_mobile: deviceInformation.isMobile,
      // 주의: 데대보에서 사용하는 프로퍼티로 변경 시 기능에 문제가 생길 수 있습니다.
      zep_device_id: deviceId
    });
  }, []);
}